import React, { useState, useEffect } from 'react';
import { clickable } from '../../../utils/a11y';
import { Link, useNavigate } from 'react-router-dom';
import './CloudStaffDashboard.css';
import cloudsafeLogo from '../../../assets/cloudsafe-logo.png';
import CloudStaffSidebar from './CloudStaffSidebar';
import {
  FaArrowLeft, FaToggleOn, FaToggleOff, FaServer, FaPlus,
  FaUserCircle, FaSignOutAlt, FaBell, FaSyncAlt, FaTimes, FaBars
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { getUserRole } from '../../../utils/roleUtils';
import { clearToken } from '../../../utils/auth';
import { getApiEndpoint } from '../../../config/api';

const FeatureFlags = () => {
  const navigate = useNavigate();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [newFlag, setNewFlag] = useState({ name: '', display_name: '', description: '', category: 'tools', enabled_global: false });

  useEffect(() => {
    const userRole = getUserRole();
    if (userRole !== 'cloud_staff' && userRole !== 'super_admin') { toast.error('Access denied'); navigate('/login'); return; }
    loadProfile(); loadFlags();
  }, [navigate]);

  const loadProfile = async () => {
    try {
      const res = await fetch(getApiEndpoint('/api/users/profile'), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) { const d = await res.json(); setUserName(`${d.firstName || d.first_name || ''} ${d.lastName || d.last_name || ''}`); }
    } catch (e) {}
  };

  const loadFlags = async () => {
    setLoading(true);
    try {
      const res = await fetch(getApiEndpoint('/api/cloud-staff/feature-flags'), { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } });
      if (res.ok) setFlags(await res.json());
    } catch (e) { toast.error('Failed to load feature flags'); }
    finally { setLoading(false); }
  };

  const toggleFlag = async (flag) => {
    try {
      const res = await fetch(getApiEndpoint(`/api/cloud-staff/feature-flags/${flag.id}`), {
        method: 'PUT',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled_global: !flag.enabled_global })
      });
      if (res.ok) {
        const updated = await res.json();
        setFlags(prev => prev.map(f => f.id === updated.id ? updated : f));
        toast.success(`${updated.display_name} ${updated.enabled_global ? 'enabled' : 'disabled'}`);
      }
    } catch (e) { toast.error('Failed to update flag'); }
  };

  const createFlag = async () => {
    if (!newFlag.name || !newFlag.display_name) { toast.warning('Name and display name required'); return; }
    try {
      const res = await fetch(getApiEndpoint('/api/cloud-staff/feature-flags'), {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(newFlag)
      });
      if (res.ok) {
        const created = await res.json();
        setFlags(prev => [...prev, created]);
        setShowCreate(false);
        setNewFlag({ name: '', display_name: '', description: '', category: 'tools', enabled_global: false });
        toast.success('Feature flag created');
      }
    } catch (e) { toast.error('Failed to create flag'); }
  };

  const handleLogout = () => { clearToken(); localStorage.removeItem('userRole'); localStorage.removeItem('token'); navigate('/login'); };

  const groupedFlags = flags.reduce((acc, f) => {
    const cat = f.category || 'general';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(f);
    return acc;
  }, {});

  return (
    <div className="cs-dashboard">
      <header className="cs-header">
        <div className="cs-header-left">
          <button className="cs-mobile-menu-btn" onClick={() => setMobileSidebarOpen(true)}><FaBars /></button>
          <img src={cloudsafeLogo} alt="CloudSafe" className="cs-logo" />
          <div className="cs-header-badge"><FaServer /><span>Cloud Staff Portal</span></div>
        </div>
        <div className="cs-header-right">
          <div className="cs-header-icons">
            <button className="cs-icon-btn"><FaBell /></button>
            <div className="cs-user-avatar" aria-haspopup="menu" aria-expanded={showUserMenu}
                 {...clickable(() => setShowUserMenu(!showUserMenu), { label: 'User menu' })}>
              <FaUserCircle />
              {showUserMenu && (
                <div className="cs-user-menu">
                  <div className="cs-user-menu-header"><FaUserCircle className="cs-menu-avatar" /><div><p className="cs-menu-name">{userName}</p><p className="cs-menu-role">Cloud Staff Admin</p></div></div>
                  <div className="cs-user-menu-divider" />
                  <button onClick={handleLogout} className="cs-menu-item cs-menu-logout"><FaSignOutAlt /> Sign Out</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>
      <div className="cs-layout">
        <CloudStaffSidebar active="feature-flags" mobileOpen={mobileSidebarOpen} onClose={() => setMobileSidebarOpen(false)} />
        <main className="cs-content">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid #21262d' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <Link to="/cloud-staff/dashboard" className="act-back-btn"><FaArrowLeft /></Link>
              <div>
                <h1 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#f0f6fc', margin: 0 }}><FaToggleOn style={{ marginRight: '0.5rem', color: '#3fb950' }} /> Feature Flags</h1>
                <p style={{ fontSize: '0.8rem', color: '#8b949e', margin: '0.2rem 0 0' }}>Toggle features on/off globally or per building</p>
              </div>
            </div>
            <button onClick={() => setShowCreate(!showCreate)} className="cs-primary-btn"><FaPlus /> New Flag</button>
          </div>

          {/* Create Form */}
          {showCreate && (
            <div style={{ background: '#161b22', border: '1px solid #30363d', borderRadius: '12px', padding: '1.25rem', marginBottom: '1.5rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                <h3 style={{ margin: 0, color: '#f0f6fc', fontSize: '1rem' }}>Create Feature Flag</h3>
                <button onClick={() => setShowCreate(false)} style={{ background: 'none', border: 'none', color: '#8b949e', cursor: 'pointer' }}><FaTimes /></button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#8b949e', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Machine Name</label>
                  <input type="text" placeholder="e.g. csv_export_v2" value={newFlag.name} onChange={(e) => setNewFlag(p => ({ ...p, name: e.target.value }))}
                    style={{ width: '100%', background: '#0d1117', border: '1px solid #30363d', borderRadius: '8px', color: '#e1e4e8', fontSize: '0.8rem', padding: '0.5rem 0.75rem', outline: 'none' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#8b949e', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Display Name</label>
                  <input type="text" placeholder="e.g. CSV Export v2" value={newFlag.display_name} onChange={(e) => setNewFlag(p => ({ ...p, display_name: e.target.value }))}
                    style={{ width: '100%', background: '#0d1117', border: '1px solid #30363d', borderRadius: '8px', color: '#e1e4e8', fontSize: '0.8rem', padding: '0.5rem 0.75rem', outline: 'none' }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#8b949e', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Category</label>
                  <select value={newFlag.category} onChange={(e) => setNewFlag(p => ({ ...p, category: e.target.value }))}
                    style={{ width: '100%', background: '#0d1117', border: '1px solid #30363d', borderRadius: '8px', color: '#e1e4e8', fontSize: '0.8rem', padding: '0.5rem 0.75rem', outline: 'none' }}>
                    <option value="tools">Tools</option><option value="monitoring">Monitoring</option><option value="analytics">Analytics</option><option value="general">General</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '0.7rem', fontWeight: 600, color: '#8b949e', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Description</label>
                  <input type="text" placeholder="What does this feature do?" value={newFlag.description} onChange={(e) => setNewFlag(p => ({ ...p, description: e.target.value }))}
                    style={{ width: '100%', background: '#0d1117', border: '1px solid #30363d', borderRadius: '8px', color: '#e1e4e8', fontSize: '0.8rem', padding: '0.5rem 0.75rem', outline: 'none' }} />
                </div>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                <button onClick={createFlag} className="cs-primary-btn">Create Flag</button>
              </div>
            </div>
          )}

          {loading ? (
            <div className="cs-loading"><div className="cs-spinner" /><p>Loading feature flags...</p></div>
          ) : (
            Object.entries(groupedFlags).map(([category, categoryFlags]) => (
              <div key={category} className="cs-section">
                <h2 className="cs-section-title" style={{ textTransform: 'capitalize' }}>{category}</h2>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {categoryFlags.map(flag => (
                    <div key={flag.id} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '1rem 1.25rem', background: '#161b22', border: '1px solid #30363d',
                      borderRadius: '10px', transition: 'border-color 0.2s'
                    }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <span style={{ fontWeight: 600, color: '#f0f6fc', fontSize: '0.9rem' }}>{flag.display_name}</span>
                          <code style={{ fontSize: '0.65rem', color: '#484f58', background: '#0d1117', padding: '0.1rem 0.4rem', borderRadius: '4px' }}>{flag.name}</code>
                        </div>
                        {flag.description && <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#8b949e' }}>{flag.description}</p>}
                      </div>
                      <button
                        onClick={() => toggleFlag(flag)}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer', fontSize: '2rem',
                          color: flag.enabled_global ? '#3fb950' : '#484f58', transition: 'color 0.2s'
                        }}
                      >
                        {flag.enabled_global ? <FaToggleOn /> : <FaToggleOff />}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </main>
      </div>
    </div>
  );
};

export default FeatureFlags;
